import type { ISceneObj } from '../../../../types/SceneType';
import type { FlowEdge, FlowNode } from '../../types/flow-types';

export type FlowAccountTier = 'free' | 'premium';
export interface FlowTierLimits {
	flows: number;
	islands: number;
}
export interface FlowEntitlementConfig {
	limits: Record<FlowAccountTier, FlowTierLimits>;
}

/** Product-configurable limits. A value of 0 means unlimited. */
export const FLOW_ENTITLEMENT_CONFIG: FlowEntitlementConfig = {
	limits: { free: { flows: 0, islands: 0 }, premium: { flows: 0, islands: 0 } },
};

export interface FlowUsage {
	flows: number;
	islands: number;
}

function values<T>(input: T[] | Record<string, T> | undefined): T[] {
	if (Array.isArray(input)) return input;
	return input && typeof input === 'object' ? Object.values(input) : [];
}

function hasRuntimeContent(input: unknown): boolean {
	if (Array.isArray(input)) return input.length > 0;
	if (input && typeof input === 'object') return Object.keys(input).length > 0;
	return Boolean(input);
}

/** Count disconnected executable components. Notes never create a runtime island. */
export function countFlowIslands(nodes: FlowNode[], edges: FlowEdge[]): number {
	const ids = new Set(nodes.filter((node) => node.type !== 'note').map((node) => String(node.id)));
	if (!ids.size) return 0;
	const neighbors = new Map([...ids].map((id) => [id, new Set<string>()]));
	for (const edge of edges) {
		const source = String(edge.source);
		const target = String(edge.target);
		if (!ids.has(source) || !ids.has(target)) continue;
		neighbors.get(source)?.add(target);
		neighbors.get(target)?.add(source);
	}
	let islands = 0;
	const visited = new Set<string>();
	for (const id of ids) {
		if (visited.has(id)) continue;
		islands += 1;
		const pending = [id];
		while (pending.length) {
			const current = pending.pop()!;
			if (visited.has(current)) continue;
			visited.add(current);
			for (const neighbor of neighbors.get(current) ?? []) {
				if (!visited.has(neighbor)) pending.push(neighbor);
			}
		}
	}
	return islands;
}

export function countSceneFlowIslands(scene: ISceneObj): number {
	const graph = (scene._meta as any)?.flowGraph;
	const nodes = values<FlowNode>(graph?.nodes);
	const edges = values<FlowEdge>(graph?.edges);
	if (nodes.length) return countFlowIslands(nodes, edges);
	return [scene.if, scene.do, scene.else].some(hasRuntimeContent) ? 1 : 0;
}

export function getFlowUsage(flows: ISceneObj[]): FlowUsage {
	return {
		flows: flows.length,
		islands: flows.reduce((total, flow) => total + countSceneFlowIslands(flow), 0),
	};
}

export function getFlowUsageWithWorkspace(
	flows: ISceneObj[],
	activeFlowId: string,
	nodes: FlowNode[],
	edges: FlowEdge[],
): FlowUsage {
	const active = flows.find((flow) => String(flow._id ?? flow.id) === String(activeFlowId));
	const saved = getFlowUsage(flows);
	const workspaceIslands = countFlowIslands(nodes, edges);
	if (active)
		return {
			flows: saved.flows,
			islands: saved.islands - countSceneFlowIslands(active) + workspaceIslands,
		};
	return {
		flows: saved.flows + (nodes.length ? 1 : 0),
		islands: saved.islands + workspaceIslands,
	};
}

export function getFlowTierLimits(isPremium: boolean, config = FLOW_ENTITLEMENT_CONFIG): FlowTierLimits {
	return config.limits[isPremium ? 'premium' : 'free'];
}
export function limitReached(used: number, limit: number): boolean {
	return limit > 0 && used >= limit;
}
export function limitExceeded(used: number, limit: number): boolean {
	return limit > 0 && used > limit;
}
export function formatFlowLimit(used: number, limit: number): string {
	return limit === 0 ? `${used} / Unlimited` : `${used} / ${limit}`;
}
